import React from "react";
import { Campaign } from "../../services";
import CampaignStatusToggle from "./CampaignStatusToggle";
import CampaignTimeDisplay from "./CampaignTimeDisplay";
import CampaignActions from "./CampaignActions";

interface CampaignTableRowProps {
  campaign: Campaign;
  onToggleStatus: (id: string) => void;
  onEdit: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
  onPageChange: (page: string) => void;
  onShowDeleteModal: (campaign: Campaign) => void;
  isAdmin: boolean;
}

const CampaignTableRow: React.FC<CampaignTableRowProps> = ({
  campaign,
  onToggleStatus,
  onEdit,
  onDelete,
  onPageChange,
  onShowDeleteModal,
  isAdmin,
}) => {
  // Kiểm tra trạng thái chiến dịch
  const now = new Date();
  const startDate = new Date(campaign.start_date);
  const endDate = new Date(campaign.end_date);

  // So sánh chỉ theo ngày, không theo giờ
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDay = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const endDay = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );

  const isNotStarted = startDay > today;
  const isExpired = endDay < today;
  const isActive = !isNotStarted && !isExpired;
  // Tính toán số lượng mã hiển thị
  // - Chiến dịch chưa diễn ra: hiển thị 0/totalCodes
  // - Chiến dịch đang diễn ra hoặc đã kết thúc: hiển thị số thực tế
  const displayUsedGifts = isNotStarted ? 0 : campaign.num_win_prizes || 0;
  // Công thức tính tổng số mã: (số lượng quà × tỷ lệ code) ÷ tỷ lệ quà
  const displayTotalGifts = campaign.total_gift || 0;
  const displayTotalCodes = Math.floor(
    (campaign.total_gift * campaign.rate_code) / campaign.rate_gift
  );

  // Consider "out of gifts" only when there is a positive total
  const isOutOfGifts =
    displayTotalGifts > 0 && displayUsedGifts >= displayTotalGifts;

  // Xác định màu sắc cho số lượng mã
  // - Màu xanh lá: Chiến dịch đang diễn ra còn mã
  // - Màu xanh dương: Chiến dịch đang diễn ra hết mã
  // - Màu đỏ: Chỉ khi chiến dịch đã kết thúc và hết mã
  // - Màu xám: Chiến dịch chưa diễn ra hoặc đã kết thúc còn mã
  const isCodeColorRed = isExpired && isOutOfGifts;
  const isCodeColorBlue = isActive && isOutOfGifts;
  const isCodeColorGreen = isActive && !isOutOfGifts;
  const isCodeColorGray = isNotStarted || (isExpired && !isOutOfGifts);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200">
      {/* Cột 1: Chiến Dịch */}
      <td className="px-4 py-4 w-28 overflow-hidden">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div
              className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2"
              title={campaign.name}
            >
              {campaign.name}
            </div>
            {campaign.description && (
              <div
                className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2"
                title={campaign.description}
              >
                {campaign.description}
              </div>
            )}
          </div>
          <span
            className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
              (campaign.type || "normal") === "voucher"
                ? "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
            }`}
          >
            {(campaign.type || "normal") === "voucher" ? "Voucher" : "Thường"}
          </span>
        </div>
      </td>
      {/* Cột 2: Link Web */}
      <td className="px-4 py-4 w-8 overflow-hidden">
        {campaign.landing_page_link ? (
          <a
            href={campaign.landing_page_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-8 h-8 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
            title={campaign.landing_page_link}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </a>
        ) : (
          <div className="inline-flex items-center justify-center w-8 h-8 text-gray-400 dark:text-gray-500">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
              />
            </svg>
          </div>
        )}
      </td>
      {/* Cột 3: Sản Phẩm */}
      <td className="px-4 py-4 w-20 overflow-hidden">
        <div
          className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2"
          title={campaign.product_name}
        >
          {campaign.product_name}
        </div>
        <div className="text-sm text-gray-900 dark:text-gray-200 truncate">
          {campaign.total_gift.toLocaleString()}
        </div>
      </td>
      {/* Cột 4: Thời Gian */}
      <td className="px-4 py-4 w-24 overflow-hidden">
        <div className="text-sm text-gray-900 dark:text-white line-clamp-2">
          <CampaignTimeDisplay campaign={campaign} />
        </div>
      </td>
      {/* Cột 5: Tỷ Lệ Đổi */}
      <td className="px-4 py-4 w-16 overflow-hidden">
        <div
          className="text-sm text-gray-900 dark:text-white truncate"
          title={`${campaign.rate_gift} quà : ${campaign.rate_code} code`}
        >
          {campaign.rate_code} code
        </div>
      </td>
      {/* Cột 6: Số Lượng Mã */}
      <td className="px-4 py-4 w-20 overflow-hidden">
        <div
          className={`text-sm truncate ${
            isCodeColorRed
              ? "text-red-600 font-medium"
              : isCodeColorBlue
              ? "text-blue-600 font-medium"
              : isCodeColorGreen
              ? "text-green-600 font-medium"
              : isCodeColorGray
              ? "text-gray-500 font-medium"
              : "text-gray-900"
          }`}
        >
          {displayTotalGifts === 0
            ? `${displayUsedGifts}/∞`
            : `${displayUsedGifts}/${displayTotalGifts}`}
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1 overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all duration-1000 ease-out ${
              isCodeColorRed
                ? "bg-red-500"
                : isCodeColorBlue
                ? "bg-blue-500"
                : isCodeColorGreen
                ? "bg-green-500"
                : isCodeColorGray
                ? "bg-gray-400 dark:bg-gray-500"
                : "bg-blue-600"
            }`}
            style={{
              width: `${
                displayTotalGifts > 0
                  ? (displayUsedGifts / displayTotalGifts) * 100
                  : 0
              }%`,
              animation: "progressFill 1.5s ease-out forwards",
            }}
          ></div>
        </div>
        {isOutOfGifts && isActive && (
          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 truncate">Hết quà</div>
        )}
      </td>
      {/* Cột 7: Trạng Thái */}
      <td className="px-4 py-4 w-16 overflow-hidden">
        <div className="flex justify-center">
          <CampaignStatusToggle campaign={campaign} onToggle={onToggleStatus} />
        </div>
      </td>
      {/* Cột 8: Thao Tác */}
      <td className="px-4 py-4 w-16 overflow-hidden text-sm font-medium">
        <CampaignActions
          campaign={campaign}
          onEdit={onEdit}
          onDelete={onDelete}
          onPageChange={onPageChange}
          onShowDeleteModal={onShowDeleteModal}
          isAdmin={isAdmin}
        />
      </td>
    </tr>
  );
};

export default CampaignTableRow;
